import { BottomSheetModalProvider } from '@gorhom/bottom-sheet';
import { useRef } from 'react';
import { ScrollView, View, useWindowDimensions } from 'react-native';

import { Box, Icon, IconButton, theme } from '@squire/ui';

import { SquireLogoText } from '@/components/_icons/SquireLogoText';
import { useWebDialogBehavior } from '@/hooks/shared/useWebDialogBehavior';

import { SINGLE_LOGO_WIDTH, styles } from './styles';
import type { PublicFlowShellProps } from './types';

// The two-column split only fits from the laptop breakpoint up; narrower viewports get the mobile column.
const PublicFlowShell = (props: PublicFlowShellProps) => {
  const {
    layout = 'split',
    branding,
    children,
    header,
    footer,
    hideBrandingOnMobile = false,
    cardOverlay,
    onOverlayClose,
    overlayLabel,
  } = props;
  // Narrowed access: `overlay` is single-layout-only, so it types away for `split` callers.
  const overlay = props.layout === 'single' ? props.overlay : undefined;
  const closeAction = props.layout === 'single' ? props.closeAction : undefined;
  const { width } = useWindowDimensions();
  const isDesktop = width >= theme.breakpoints.laptop;
  const hasOverlay = !!cardOverlay;
  const hasSingleOverlay = !!overlay;
  const overlayRef = useRef<View>(null);

  // The card-scoped drawer is a modal dialog; native sheets manage their own focus.
  useWebDialogBehavior(overlayRef, {
    active: hasOverlay,
    onDismiss: onOverlayClose,
  });

  // Hides a card region from assistive tech and blocks touches while the drawer is open.
  const hiddenBehindOverlay = hasOverlay
    ? {
        accessibilityElementsHidden: true,
        importantForAccessibility: 'no-hide-descendants' as const,
        'aria-hidden': true,
        pointerEvents: 'none' as const,
      }
    : {};

  const hiddenBehindSingleOverlay = hasSingleOverlay
    ? {
        accessibilityElementsHidden: true,
        importantForAccessibility: 'no-hide-descendants' as const,
        'aria-hidden': true,
        pointerEvents: 'none' as const,
      }
    : {};

  return (
    <BottomSheetModalProvider>
      {layout === 'single' ? (
        <Box style={styles.singleRoot} testID="public-flow-shell-single">
          {closeAction ? (
            <Box
              style={styles.singleHeader}
              testID="public-flow-shell-header"
              {...hiddenBehindSingleOverlay}
            >
              <Box style={styles.singleLogo}>
                <SquireLogoText size={SINGLE_LOGO_WIDTH} color="textPrimary" />
              </Box>
              <IconButton
                size="xl"
                state="hover"
                icon={<Icon name="close" />}
                onPress={closeAction.onPress}
                disabled={closeAction.disabled}
                accessibilityLabel={closeAction.accessibilityLabel}
                accessibilityHint={closeAction.accessibilityHint}
                testID="public-flow-shell-close"
              />
            </Box>
          ) : null}
          <ScrollView
            testID="public-flow-shell-single-scroll"
            style={styles.singleScroll}
            contentContainerStyle={[
              styles.singleScrollContent,
              closeAction ? styles.singleScrollContentUnderHeader : null,
            ]}
            // react-native-web maps this to `scrollbarWidth: 'none'`; scrolling itself is unaffected.
            showsVerticalScrollIndicator={false}
          >
            {closeAction ? null : (
              <Box style={styles.singleLogo}>
                <SquireLogoText size={SINGLE_LOGO_WIDTH} color="textPrimary" />
              </Box>
            )}
            <Box style={styles.singleContent} {...hiddenBehindSingleOverlay}>
              {children}
            </Box>
            {footer ? (
              <Box style={styles.singleFooter} {...hiddenBehindSingleOverlay}>
                {footer}
              </Box>
            ) : null}
          </ScrollView>
          {overlay ? (
            <Box style={styles.singleOverlayHost}>{overlay}</Box>
          ) : null}
        </Box>
      ) : isDesktop ? (
        <Box style={styles.desktopRoot} testID="public-flow-shell-desktop">
          <Box style={styles.brandingColumn}>{branding}</Box>
          <Box style={styles.card}>
            {header ? (
              <Box style={styles.cardHeader} {...hiddenBehindOverlay}>
                {header}
              </Box>
            ) : null}
            <Box
              style={[
                styles.cardContent,
                !header && styles.cardContentNoHeader,
              ]}
              {...hiddenBehindOverlay}
            >
              {children}
            </Box>
            {footer ? (
              <Box style={styles.cardFooter} {...hiddenBehindOverlay}>
                {footer}
              </Box>
            ) : null}
            {cardOverlay ? (
              <View
                ref={overlayRef}
                role="dialog"
                aria-modal
                aria-label={overlayLabel}
                style={styles.overlayHost}
              >
                {cardOverlay}
              </View>
            ) : null}
          </Box>
        </Box>
      ) : (
        <Box style={styles.mobileRoot} testID="public-flow-shell-mobile">
          <Box style={styles.mobileContent}>
            {hideBrandingOnMobile ? null : branding}
            <Box style={styles.mobileStep}>{children}</Box>
          </Box>
          {footer ? <Box style={styles.mobileFooter}>{footer}</Box> : null}
        </Box>
      )}
    </BottomSheetModalProvider>
  );
};

PublicFlowShell.displayName = 'PublicFlowShell';
export { PublicFlowShell };
